import { useMemo } from 'react';
import moment from 'moment';
import classNames from 'classnames';
import Skeleton from 'react-loading-skeleton';
import { TMessageCampaign } from '~/view/screens/MarketingScreen/screens/MessageCampaign/CampaignListScreen/types';
import { history } from '~/services/history';
import { SCREENS } from '~/constants/screens';
import DeleteRestoreBlock from '~/view/components/DeleteRestoreBlock/DeleteRestoreBlock';
import { confirmDelete } from '~/utils/modalConfirmations';
import { localeStore } from '~/mobx/localesStore';
import './style.scss';
import { messageCampaignListStore } from '~/view/screens/MarketingScreen/screens/MessageCampaign/CampaignListScreen/store/messageCampaignListStore';
import { PostingTime } from '~/view/screens/MarketingScreen/screens/MessageCampaign/types';
import { defaultDateFormat } from '~/constants/dateTime';

type ListFieldProps = {
  text?: string;
  className: string;
};

const ListField = ({ text, className }: ListFieldProps) => (
  <div className={classNames('d-flex flex-row align-items-center pr-2', className)}>
    <p className="campaign-list-item__field">{text ?? <Skeleton width={100} />}</p>
  </div>
);

type MessageCampaignListRowProps = {
  campaign?: TMessageCampaign;
};

const MessageCampaignListRow = ({ campaign }: MessageCampaignListRowProps) => {
  const handleClick = () => {
    if (campaign?.id) history.push(SCREENS.MESSAGE_CAMPAIGN_EDIT(campaign.id));
  };

  const handleDelete = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    confirmDelete({
      title: localeStore.t('marketing_mc_delete_comfirmation_header'),
      message: localeStore.t('marketing_mc_delete_comfirmation_bodytext'),
      onConfirm: () => messageCampaignListStore.deleteCampaign(campaign?.id || 0),
    });
  };

  const scheduledTime = useMemo((): string => {
    if (!campaign?.start_at) return '';

    const { posting_type, start_at, end_at, settings } = campaign;
    const time = settings?.[0]?.time;

    const formatTime = (time: Date) => moment.utc(time, 'hh:mm:ss').local().format('h:mm A');
    const formatDate = (date: string) => moment(date).format(defaultDateFormat);

    switch (posting_type) {
      case PostingTime.NOW:
      case PostingTime.LATER:
        return `${formatDate(start_at)}, ${formatTime(time)}`;
      case PostingTime.SCHEDULED:
        return `${formatDate(start_at)} - ${formatDate(end_at)}, ${localeStore.t('marketing_mc_table_scheduled_time_repeated')}`;
      default:
        return '';
    }
  }, [campaign]);

  return (
    <div className="campaign-list-item d-flex flex-row full-width align-items-center cursor-pointer" onClick={handleClick}>
      <ListField text={campaign?.title} className="w-20" />
      <ListField text={campaign?.message} className="w-40" />
      <ListField text={campaign?.user_name} className="w-15" />
      <ListField text={scheduledTime} className="w-20" />
      {campaign ? (
        <div className="d-flex justify-content-end w-5">
          <DeleteRestoreBlock onDelete={handleDelete} />
        </div>
      ) : null}
    </div>
  );
};

export default MessageCampaignListRow;
